import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import { rectContains, type Point, type Rect, type Size } from "../../common/geometry.js";
import { visibleRowRange } from "../core/virtualWindow.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { drawAlignedText, type TextAlign } from "./Label.js";
import { ScrollableBase, type ScrollableOptions } from "./ScrollableBase.js";
import type { SelectionMode } from "./ListBox.js";

export interface ListViewColumn {
  text: string;
  width: number;
  minWidth?: number;
  align?: TextAlign;
}

export interface ListViewOptions<T = unknown> extends ScrollableOptions {
  columns?: readonly ListViewColumn[];
  items?: readonly T[];
  selectionMode?: SelectionMode;
  rowHeight?: number;
  font?: FontSpec;
  showGridLines?: boolean;
  /** Text for a cell; defaults to indexing an array or object by column. */
  cellText?: (item: T, columnIndex: number, column: ListViewColumn) => string;
  onSelectionChanged?: () => void;
  /** A header was clicked — the app decides whether that means sort. */
  onColumnClick?: (columnIndex: number) => void;
}

/** How close to a column divider counts as grabbing it. */
const DIVIDER_SLOP = 4;

function defaultCellText(item: unknown, columnIndex: number, column: ListViewColumn): string {
  if (Array.isArray(item)) return String(item[columnIndex] ?? "");
  if (item && typeof item === "object") {
    const record = item as Record<string, unknown>;
    const byKey = record[column.text];
    if (byKey !== undefined) return String(byKey);
  }
  return columnIndex === 0 ? String(item) : "";
}

/**
 * A multi-column report-style list with a resizable header.
 *
 * Rows are virtualized like ListBox, and the header is painted by the control
 * rather than being a child, so it can pin above the viewport while still
 * scrolling horizontally with the columns.
 */
export class ListView<T = unknown> extends ScrollableBase {
  #columns: ListViewColumn[];
  #items: T[];
  #selected = new Set<number>();
  #anchor = -1;
  #focusIndex = -1;
  #hoverIndex = -1;
  #hoverColumn = -1;
  #pressedColumn = -1;
  #resizing: { column: number; startX: number; startWidth: number } | null = null;
  #rowHeight: number | null;
  #font: FontSpec | null;

  selectionMode: SelectionMode;
  showGridLines: boolean;
  cellText: (item: T, columnIndex: number, column: ListViewColumn) => string;

  constructor(options: ListViewOptions<T> = {}) {
    super({ width: 380, height: 200, tabStop: true, ...options });
    this.#columns = options.columns ? options.columns.map((c) => ({ ...c })) : [];
    this.#items = options.items ? [...options.items] : [];
    this.selectionMode = options.selectionMode ?? "single";
    this.#rowHeight = options.rowHeight ?? null;
    this.#font = options.font ?? null;
    this.showGridLines = options.showGridLines ?? true;
    this.cellText =
      (options.cellText as ListView<T>["cellText"] | undefined) ??
      ((item, i, column) => defaultCellText(item, i, column));
    if (options.onSelectionChanged) this.on("selectionchanged", options.onSelectionChanged);
    this.#columnClick = options.onColumnClick ?? null;

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", (e) => this.#onPointerUp(e));
    this.on("pointerleave", () => {
      this.#hoverIndex = -1;
      this.#hoverColumn = -1;
      this.invalidate();
    });
    this.on("doubleclick", (e) => this.#onDoubleClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  #columnClick: ((columnIndex: number) => void) | null = null;

  // --- model -----------------------------------------------------------------

  get columns(): readonly ListViewColumn[] {
    return this.#columns;
  }
  set columns(v: readonly ListViewColumn[]) {
    this.#columns = v.map((c) => ({ ...c }));
    this.updateScrollBars();
    this.invalidate();
  }

  get items(): readonly T[] {
    return this.#items;
  }
  set items(v: readonly T[]) {
    // Only claim the selection changed if it actually did. Emitting
    // unconditionally lets a handler that reacts by refilling the list drive
    // itself round in circles.
    const hadSelection = this.#selected.size > 0;
    this.#items = [...v];
    this.#selected.clear();
    this.#focusIndex = this.#anchor = -1;
    this.updateScrollBars();
    this.clampScroll();
    if (hadSelection) this.emit("selectionchanged");
    this.invalidate();
  }

  get count(): number {
    return this.#items.length;
  }

  get selectedIndex(): number {
    let min = -1;
    for (const i of this.#selected) if (min < 0 || i < min) min = i;
    return min;
  }
  set selectedIndex(v: number) {
    if (v < 0) {
      this.#setSelection([]);
      return;
    }
    this.#setSelection([v]);
    this.#focusIndex = this.#anchor = v;
    this.ensureVisible(v);
  }

  get selectedIndices(): number[] {
    return [...this.#selected].sort((a, b) => a - b);
  }
  /**
   * Replace the whole selection.
   *
   * Filtered by `selectionMode` rather than obeyed literally: a caller that
   * hands three indices to a single-selection list should end up in a state the
   * user could also have reached by clicking, since anything else leaves a
   * selection that cannot be reproduced — or undone — by hand.
   */
  set selectedIndices(v: readonly number[]) {
    const wanted =
      this.selectionMode === "none" ? []
      : this.selectionMode === "single" ? v.slice(0, 1)
      : v;
    this.#setSelection(wanted);
    // The lowest index, not the first one given: it is where the block starts,
    // so shift-clicking after a programmatic set extends from the top of it.
    const first = this.selectedIndex;
    this.#focusIndex = this.#anchor = first;
    if (first >= 0) this.ensureVisible(first);
  }

  get selectedItem(): T | undefined {
    const i = this.selectedIndex;
    return i >= 0 ? this.#items[i] : undefined;
  }

  isSelected(index: number): boolean {
    return this.#selected.has(index);
  }

  /** Select every row. No-op unless the mode is "multiple", as on ListBox. */
  selectAll(): void {
    if (this.selectionMode !== "multiple") return;
    this.#setSelection(range(0, this.#items.length - 1));
  }

  #setSelection(indices: readonly number[]): void {
    const next = new Set(indices.filter((i) => i >= 0 && i < this.#items.length));
    if (next.size === this.#selected.size && [...next].every((i) => this.#selected.has(i))) return;
    this.#selected = next;
    this.emit("selectionchanged");
    this.invalidate();
  }

  // --- geometry ----------------------------------------------------------------

  get rowHeight(): number {
    return this.#rowHeight ?? this.desktop?.theme.metrics.listItemHeight ?? 20;
  }
  set rowHeight(v: number | null) {
    this.#rowHeight = v;
    this.updateScrollBars();
    this.invalidate();
  }

  get headerHeight(): number {
    return this.#columns.length > 0 ? (this.desktop?.theme.metrics.headerHeight ?? 20) : 0;
  }

  protected override viewportInsetTop(theme: Theme): number {
    return this.#columns.length > 0 ? theme.metrics.headerHeight : 0;
  }

  protected override contentSize(_theme: Theme): Size {
    return { width: this.totalColumnWidth, height: this.#items.length * this.rowHeight };
  }

  get totalColumnWidth(): number {
    let total = 0;
    for (const c of this.#columns) total += c.width;
    return total;
  }

  /** Header strip in local coordinates. */
  get headerRect(): Rect {
    const theme = this.desktop?.theme;
    if (!theme || this.#columns.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    const inset = this.contentInset(theme);
    const view = this.viewportRect;
    return { x: inset, y: inset, width: view.width, height: theme.metrics.headerHeight };
  }

  /** x offset of a column's left edge in content coordinates. */
  columnOffset(index: number): number {
    let x = 0;
    for (let i = 0; i < index && i < this.#columns.length; i++) x += this.#columns[i]!.width;
    return x;
  }

  /** Column index whose divider is within DIVIDER_SLOP of a local x, or -1. */
  #dividerAt(localX: number): number {
    const view = this.viewportRect;
    let x = view.x - this.scrollOffsetX;
    for (let i = 0; i < this.#columns.length; i++) {
      x += this.#columns[i]!.width;
      if (Math.abs(localX - x) <= DIVIDER_SLOP) return i;
    }
    return -1;
  }

  #columnAt(localX: number): number {
    const view = this.viewportRect;
    let x = view.x - this.scrollOffsetX;
    for (let i = 0; i < this.#columns.length; i++) {
      const w = this.#columns[i]!.width;
      if (localX >= x && localX < x + w) return i;
      x += w;
    }
    return -1;
  }

  /** Row index at a local point, or -1 when the point is not over a row. */
  indexAt(p: Point): number {
    const view = this.viewportRect;
    if (!rectContains(view, p)) return -1;
    const y = p.y - view.y + this.scrollOffsetY;
    const index = Math.floor(y / this.rowHeight);
    return index >= 0 && index < this.#items.length ? index : -1;
  }

  ensureVisible(index: number): void {
    if (index < 0 || index >= this.#items.length) return;
    this.scrollIntoView({
      x: this.scrollOffsetX,
      y: index * this.rowHeight,
      width: 0,
      height: this.rowHeight,
    });
  }

  /** Grow or shrink a column, respecting its minimum. */
  setColumnWidth(index: number, width: number): void {
    const column = this.#columns[index];
    if (!column) return;
    const next = Math.max(column.minWidth ?? 32, Math.round(width));
    if (next === column.width) return;
    column.width = next;
    this.updateScrollBars();
    this.clampScroll();
    this.invalidate();
  }

  override cursorAt(p: Point): string {
    if (this.#resizing) return "col-resize";
    if (rectContains(this.headerRect, p) && this.#dividerAt(p.x) >= 0) return "col-resize";
    return this.cursor;
  }

  // --- input -----------------------------------------------------------------------

  #onPointerDown(e: MouseEventArgs): void {
    this.focus();
    const p = { x: e.x, y: e.y };

    if (rectContains(this.headerRect, p)) {
      e.handled = true;
      const divider = this.#dividerAt(e.x);
      if (divider >= 0) {
        this.#resizing = {
          column: divider,
          startX: e.screen.x,
          startWidth: this.#columns[divider]!.width,
        };
        this.desktop?.input.setCapture(this);
        return;
      }
      this.#pressedColumn = this.#columnAt(e.x);
      this.desktop?.input.setCapture(this);
      this.invalidate();
      return;
    }

    const index = this.indexAt(p);
    if (index < 0 || this.selectionMode === "none") return;
    e.handled = true;
    this.#focusIndex = index;

    if (this.selectionMode === "multiple" && e.shiftKey && this.#anchor >= 0) {
      this.#setSelection(range(this.#anchor, index));
    } else if (this.selectionMode === "multiple" && e.ctrlKey) {
      const next = new Set(this.#selected);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      this.#anchor = index;
      this.#setSelection([...next]);
    } else {
      this.#anchor = index;
      this.#setSelection([index]);
    }
    this.ensureVisible(index);
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (this.#resizing) {
      this.setColumnWidth(
        this.#resizing.column,
        this.#resizing.startWidth + (e.screen.x - this.#resizing.startX),
      );
      return;
    }
    const p = { x: e.x, y: e.y };
    const overHeader = rectContains(this.headerRect, p);
    const nextColumn = overHeader ? this.#columnAt(e.x) : -1;
    const nextRow = overHeader ? -1 : this.indexAt(p);
    if (nextColumn !== this.#hoverColumn || nextRow !== this.#hoverIndex) {
      this.#hoverColumn = nextColumn;
      this.#hoverIndex = nextRow;
      this.invalidate();
    }
  }

  #onPointerUp(e: MouseEventArgs): void {
    if (this.#resizing) {
      this.#resizing = null;
      this.desktop?.input.releaseCapture(this);
      return;
    }
    if (this.#pressedColumn >= 0) {
      const released = this.#columnAt(e.x);
      const clicked = this.#pressedColumn;
      this.#pressedColumn = -1;
      this.desktop?.input.releaseCapture(this);
      this.invalidate();
      if (released === clicked && clicked >= 0 && rectContains(this.headerRect, { x: e.x, y: e.y })) {
        this.#columnClick?.(clicked);
      }
    }
  }

  #onDoubleClick(e: MouseEventArgs): void {
    const p = { x: e.x, y: e.y };
    if (rectContains(this.headerRect, p)) {
      // Double-clicking a divider auto-sizes the column to its widest cell.
      const divider = this.#dividerAt(e.x);
      if (divider >= 0) {
        this.autoSizeColumn(divider);
        e.handled = true;
      }
      return;
    }
    const index = this.indexAt(p);
    if (index < 0) return;
    this.emit("itemactivate", index);
    e.handled = true;
  }

  /** Fit a column to the widest of its header and cell texts. */
  autoSizeColumn(index: number): void {
    const desktop = this.desktop;
    const column = this.#columns[index];
    if (!desktop || !column) return;
    const theme = desktop.theme;
    const font = this.#font ?? theme.metrics.font;
    let max = desktop.rc.measureText(column.text, theme.metrics.font).width;
    for (const item of this.#items) {
      const w = desktop.rc.measureText(this.cellText(item, index, column), font).width;
      if (w > max) max = w;
    }
    this.setColumnWidth(index, Math.ceil(max) + theme.metrics.columnPadding * 2 + 4);
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (this.#items.length === 0) return;
    const view = this.viewportRect;
    const page = Math.max(1, Math.floor(view.height / this.rowHeight) - 1);
    const current = this.#focusIndex >= 0 ? this.#focusIndex : this.selectedIndex;
    let next = current;

    switch (e.key) {
      case "ArrowUp":
        next = current <= 0 ? 0 : current - 1;
        break;
      case "ArrowDown":
        next = current < 0 ? 0 : Math.min(this.#items.length - 1, current + 1);
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = this.#items.length - 1;
        break;
      case "PageUp":
        next = Math.max(0, (current < 0 ? 0 : current) - page);
        break;
      case "PageDown":
        next = Math.min(this.#items.length - 1, (current < 0 ? 0 : current) + page);
        break;
      case "Enter":
        if (current >= 0) {
          this.emit("itemactivate", current);
          e.handled = true;
        }
        return;
      case "a":
      case "A":
        if (e.ctrlKey && this.selectionMode === "multiple") {
          this.selectAll();
          e.handled = true;
        }
        return;
      default:
        return;
    }

    e.handled = true;
    this.#focusIndex = next;
    if (this.selectionMode === "multiple" && e.shiftKey && this.#anchor >= 0) {
      this.#setSelection(range(this.#anchor, next));
    } else if (this.selectionMode !== "none") {
      this.#anchor = next;
      this.#setSelection([next]);
    }
    this.ensureVisible(next);
  }

  // --- painting ---------------------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    theme.drawListBackground(rc, this.localRect, state);
    this.#paintRows(rc, theme, state);
    this.#paintHeader(rc, theme, state);
  }

  #paintRows(rc: RenderContext, theme: Theme, state: VisualState): void {
    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const font = this.#font ?? theme.metrics.font;
    const rowHeight = this.rowHeight;
    const pad = theme.metrics.columnPadding;

    rc.clipped(view, () => {
      const { first, last } = visibleRowRange(
        this.scrollOffsetY,
        view.height,
        rowHeight,
        0,
        this.#items.length,
      );
      const rowWidth = Math.max(view.width, this.totalColumnWidth);

      for (let i = first; i <= last; i++) {
        const y = view.y + i * rowHeight - this.scrollOffsetY;
        const row: Rect = { x: view.x - this.scrollOffsetX, y, width: rowWidth, height: rowHeight };
        const rowState: VisualState = {
          ...state,
          hovered: this.#hoverIndex === i,
          selected: this.#selected.has(i),
          active: this.focused,
          focused: this.#focusIndex === i && this.focused,
        };
        theme.drawListItem(rc, row, rowState);

        const ink = theme.listItemTextColor(rowState);
        let x = row.x;
        for (let c = 0; c < this.#columns.length; c++) {
          const column = this.#columns[c]!;
          const cell: Rect = { x: x + pad, y, width: Math.max(0, column.width - pad * 2), height: rowHeight };
          if (cell.x < view.x + view.width && cell.x + cell.width > view.x) {
            drawAlignedText(
              rc,
              this.cellText(this.#items[i]!, c, column),
              cell,
              font,
              ink,
              column.align ?? "left",
              "middle",
              true,
            );
          }
          x += column.width;
          if (this.showGridLines && !rowState.selected) {
            rc.vLine(x - 1, y, rowHeight, theme.palette.gridLine);
          }
        }
        if (this.showGridLines) {
          rc.hLine(row.x, y + rowHeight - 1, rowWidth, theme.palette.gridLine);
        }
        if (rowState.focused && !rowState.selected) theme.drawFocusRing(rc, row);
      }
    });
  }

  #paintHeader(rc: RenderContext, theme: Theme, state: VisualState): void {
    if (this.#columns.length === 0) return;
    const header = this.headerRect;
    if (header.width <= 0) return;

    rc.clipped(header, () => {
      let x = header.x - this.scrollOffsetX;
      for (let i = 0; i < this.#columns.length; i++) {
        const column = this.#columns[i]!;
        const cell: Rect = { x, y: header.y, width: column.width, height: header.height };
        theme.drawColumnHeader(rc, cell, {
          ...state,
          hovered: this.#hoverColumn === i,
          pressed: this.#pressedColumn === i,
        });
        const pad = theme.metrics.columnPadding;
        const nudge = this.#pressedColumn === i ? 1 : 0;
        drawAlignedText(
          rc,
          column.text,
          {
            x: cell.x + pad + nudge,
            y: cell.y + nudge,
            width: Math.max(0, cell.width - pad * 2),
            height: cell.height,
          },
          theme.metrics.font,
          theme.palette.headerText,
          column.align ?? "left",
          "middle",
          true,
        );
        x += column.width;
      }
      // Fill any gap past the last column so the header spans the full width.
      if (x < header.x + header.width) {
        theme.drawColumnHeader(
          rc,
          { x, y: header.y, width: header.x + header.width - x, height: header.height },
          { ...state, hovered: false, pressed: false },
        );
      }
    });
  }

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    this.paintScrollCorner(rc, theme);
  }

  /**
   * Only the column dividers. Everywhere else a one-finger drag scrolls,
   * which is the whole point of having rows.
   */
  override handlesDragAt(p: Point): boolean {
    return this.cursorAt(p) === "col-resize";
  }
}

function range(a: number, b: number): number[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const out: number[] = [];
  for (let i = lo; i <= hi; i++) out.push(i);
  return out;
}
